import { DocTypeEnum } from '@cognitivecredit/ui-utils'
import type { AutoPopulatePayload } from '@/components/admin/documents/upload/types.ts'

export const docNameRegex = '([A-Z]+)_((?:[1-4][QH])|FY)([0-9]{2})'

export const docTypes = Object.fromEntries(Object.values(DocTypeEnum).map((value) => [value, value])) as Record<
  DocTypeEnum,
  DocTypeEnum
>

export const PERIOD_BASED_DOC_TYPES = [
  DocTypeEnum.Financials,
  DocTypeEnum.Prelims,
  DocTypeEnum.Presentation,
  DocTypeEnum.Sustainability,
] as const

export const ALL_DOC_TYPES = [...PERIOD_BASED_DOC_TYPES, DocTypeEnum.Prospectus, DocTypeEnum.Other] as const

export const isValidFinancialDoc = (type: string, name: string) => {
  return type === DocTypeEnum.Financials && hasFinancialDocName(name)
}

export const isValidPrelimDoc = (type: string, name: string) => {
  return type === DocTypeEnum.Prelims && hasPrelimDocName(name)
}

export const isValidPresentationDoc = (type: string, name: string) => {
  return type === DocTypeEnum.Presentation && hasPresentationDocName(name)
}

export const isValidSustainabilityDoc = (type: string, name: string) => {
  return type === DocTypeEnum.Sustainability && hasSustainabilityDocName(name)
}

export const isValidProspectusDoc = (type: string, name: string) => {
  return type === DocTypeEnum.Prospectus && hasProspectusDocName(name)
}

export const isValidOtherDoc = (type: string, name: string) => {
  return (
    type === DocTypeEnum.Other &&
    !(
      hasFinancialDocName(name) ||
      hasPrelimDocName(name) ||
      hasPresentationDocName(name) ||
      hasSustainabilityDocName(name) ||
      hasProspectusDocName(name)
    )
  )
}

export const hasFinancialDocName = (name: string) => {
  return name.match(`${docNameRegex}.pdf`)
}

export const hasPrelimDocName = (name: string) => {
  return name.match(`${docNameRegex}_prelim.pdf`)
}

export const hasPresentationDocName = (name: string) => {
  return name.match(`${docNameRegex}_P.pdf`)
}

export const hasSustainabilityDocName = (name: string) => {
  return name.match(`${docNameRegex}_S.pdf`)
}

export const hasProspectusDocName = (name: string) => {
  return name.match('([A-Z]+)_([0-9]{4})([0-9]{2}).pdf')
}

export const hasConventionalDocName = (name: string) => {
  return !!(
    hasFinancialDocName(name) ||
    hasPrelimDocName(name) ||
    hasPresentationDocName(name) ||
    hasSustainabilityDocName(name) ||
    hasProspectusDocName(name)
  )
}

export const createAutopopulatePayload = async (
  item: { name: string; sourceUrl?: string },
  getCompanyId: (ticker: string) => Promise<string>,
) => {
  const payload: AutoPopulatePayload = {
    documentName: item.name,
    sourceUrl: item.sourceUrl,
  }

  const isFinancials = hasFinancialDocName(payload.documentName)
  const isPreliminary = hasPrelimDocName(payload.documentName)
  const isPresentation = hasPresentationDocName(payload.documentName)
  const isSustainability = hasSustainabilityDocName(payload.documentName)
  const isProspectus = hasProspectusDocName(payload.documentName)

  if (isPresentation) {
    payload.companyId = await getCompanyId(isPresentation[1])
    payload.documentType = 'presentation'
    payload.year = `20${isPresentation[3]}`
    payload.period = isPresentation[2]
    payload.conventionalName = true
  } else if (isFinancials) {
    payload.companyId = await getCompanyId(isFinancials[1])
    payload.documentType = 'financials'
    payload.year = `20${isFinancials[3]}`
    payload.period = isFinancials[2]
    payload.conventionalName = true
  } else if (isPreliminary) {
    payload.companyId = await getCompanyId(isPreliminary[1])
    payload.documentType = 'preliminaries'
    payload.year = `20${isPreliminary[3]}`
    payload.period = isPreliminary[2]
    payload.conventionalName = true
  } else if (isProspectus) {
    payload.companyId = await getCompanyId(isProspectus[1])
    payload.documentType = 'prospectus'
    payload.year = isProspectus[2]
    payload.month = isProspectus[3]
    payload.pageRange = '0'
    payload.conventionalName = true
  } else if (isSustainability) {
    payload.companyId = await getCompanyId(isSustainability[1])
    payload.documentType = 'sustainability'
    payload.year = `20${isSustainability[3]}`
    payload.period = isSustainability[2]
    payload.pageRange = '0'
    payload.conventionalName = true
  } else {
    const [ticker] = payload.documentName.split('_')
    payload.companyId = await getCompanyId(ticker)
    payload.documentType = 'other'
    payload.pageRange = '0'
    payload.conventionalName = false
  }
  return payload
}

export const createDocumentList = async (files: [], getCompanyId: (ticker: string) => Promise<string>) => {
  return Promise.all([].map.call(files, async (file) => await createAutopopulatePayload(file, getCompanyId))).then(
    (data) => {
      return data
    },
  )
}
